'use client';

import {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';
import { useTranslation } from 'next-i18next';
import {
  ChatTypingDots, Button,
} from '@components/common';
import { useChatComposer } from '@hooks/useChatComposer/useChatComposer';
import {
  PaperAirplaneIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon,
} from '@heroicons/react/24/solid';
import { ChatTextarea } from '@components/common/Chat/ChatTextarea';
import { Message, useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { v4 as uuidv4 } from 'uuid';
import { ChatMessageComponent } from '@components/templates/homepage/chat/ChatMessageComponent';
import { ChatJobModal } from '@components/templates/homepage/chat/ChatJobModal';
import ChatTalentModal from '@components/templates/homepage/chat/ChatTalentModal';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { useAppSelector } from '@store/index';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import NiceModal from '@ebay/nice-modal-react';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';
import { cn } from '@utils/cn';
import { Transition } from '@headlessui/react';
import { CandidateFloatingNavBar } from './CandidateFloatingNavBar';
import { showVoiceAgentModalWithAuth } from '@utils/voice-agent/showVoiceAgentModalWithAuth';
import { useSwrResumePersonalData } from '@hooks/useSwrApi/endpoints/resume/useSwrResumePersonalData';
import { useSwrResumeLastWorkExperience } from '@hooks/useSwrApi/endpoints/resume';
import { useSwrJobSearchTooltip } from '@hooks/useSwrApi/endpoints/job';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';

const agent = { id: '' };

interface MissionControlBestieContentProps {
  locale: Locale;
  isAuthenticated?: boolean;
}

/**
 * @description Bestie chat experience for the Mission Control default (new-search) view.
 * Replicates the /bestie page — title + input + chips initially, then transitions
 * to a chat view — but without StoriesSection / CompaniesSlider and with the form
 * anchored inside the MC content column (not fixed to the full viewport).
 */
export const MissionControlBestieContent = ({ locale, isAuthenticated = false }: MissionControlBestieContentProps) => {
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  const { stream, startStream } = useStreamStateV2(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const anonSessionId = useAnonSessionId();
  const [talentsNavigationList, setTalentsNavigationList] = useState<ApiSchema<'ResumeCardData'>[]>([]);
  const [hasSubmittedFirstMessage, setHasSubmittedFirstMessage] = useState(false);
  const [firstMessageContent, setFirstMessageContent] = useState<string | null>(null);
  const [expandState, setExpandState] = useState<{
    showToggle: boolean; isExpanded: boolean; toggle:() => void;
  } | null>(null);

  const headers = useMemo(() => ({
    'x-anon-session-id': anonSessionId ?? '',
  }), [anonSessionId]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const sendMessage = useCallback(async (text: string) => {
    if (!hasSubmittedFirstMessage) setFirstMessageContent(text);
    setHasSubmittedFirstMessage(true);
    const message = { id: uuidv4(), content: text, type: 'human' } as const;
    await startStream(message, agent.id, {
      headers,
      configurations: { user_locale: locale || 'ro' },
    });
  }, [hasSubmittedFirstMessage, startStream, headers, locale]);

  const composer = useChatComposer({
    history: stream?.messages,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    onSubmit: (text) => { void sendMessage(text); },
  });

  // Clear first-message bubble once the stream starts delivering
  useEffect(() => {
    if (stream?.messages && stream.messages.length > 0) {
      setFirstMessageContent(null);
    }
  }, [stream?.messages]);

  const jobsArray = useMemo(() => stream?.messages
    ?.filter((item) => (item?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0)
    .reduce<{ items: ApiSchema<'JobItem'>[] | undefined }[]>((acc, item) => {
    acc.push({ items: item?.response_metadata?.customData?.jobSearchResults?.items?.map((i) => i.data) });
    return acc;
  }, []), [stream?.messages]);

  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);
  useEffect(() => {
    if (bulkApplyFeed?.length) {
      NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale }).catch(() => {});
    }
  }, [bulkApplyFeed, locale]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const onCvDetailOpen = useCallback((message: Message) => {
    setTalentsNavigationList(message.response_metadata?.customData?.resumeSearchResults?.items || []);
  }, []);

  const messagesList = useMemo(() => {
    if (!stream?.messages) return [];

    // Use a Map to remove duplicates based on message id
    const reversed = [...new Map(stream.messages.map((m) => [m.id, m])).values()].reverse();

    // In flex-col-reverse layout, DOM index 0 = visually bottom, index N = visually top.
    // Tool results arrive before the AI text in streaming, so after reversing, text ends up
    // at index 0 (bottom) and results at index 1 (above). Swap consecutive pairs so text
    // appears above results in the UI.
    for (let i = 0; i < reversed.length - 1; i += 1) {
      const curr = reversed[i];
      const next = reversed[i + 1];
      const currHasResults = (curr?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
        || (curr?.response_metadata?.customData?.resumeSearchResults?.items?.length ?? 0) > 0;
      const nextHasResults = (next?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
        || (next?.response_metadata?.customData?.resumeSearchResults?.items?.length ?? 0) > 0;
      if (!currHasResults && curr?.type === 'ai' && nextHasResults) {
        reversed[i] = next;
        reversed[i + 1] = curr;
      }
    }

    return reversed.map((m, index) => (
      <div key={`message-container-${m.id}`} className="flex flex-col gap-4">
        <ChatMessageComponent
          key={`message-${m.id}`}
          message={{ ...m }}
          ref={index === 0 ? lastMessageRef : null}
          locale={locale}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          onInteraction={onCvDetailOpen}
        />
      </div>
    ));
  }, [stream?.messages, locale, onCvDetailOpen]);

  const formElements = (
    <div className="mt-6 w-full max-w-5xl">
      <div className="flex w-full items-end gap-2 rounded-[24px] bg-surface p-6 shadow-lg backdrop-blur-sm transition-all duration-700">
        <ChatTextarea
          required={false}
          textareaRef={composer.textareaRef}
          input={composer.input}
          handleInputChange={composer.onChange}
          handleKeyDown={composer.onKeyDown}
          placeholder={hasSubmittedFirstMessage ? t('homepage.chat.input.placeholder') : t('homepage.chat.input.placeholder.mc')}
          customWrapperStyle="flex-1 rounded-2xl"
          customTextareaStyle="transition-none bg-transparent !text-ink placeholder:!text-ink/75 placeholder:opacity-100 placeholder:text-base placeholder:sm:text-xl px-2 text-base sm:text-lg focus:ring-0 focus:ring-offset-0 outline-none border-none shadow-none whitespace-normal resize-none cursor-text pointer-events-auto leading-normal py-1.5 break-words"
          textareaSizeStyle="w-full min-h-10"
          textareaWrapperSizeStyle="w-full min-h-10"
          textareaAlignmentStyle="px-2 py-2.5"
          textareaWrapperAlignmentStyle="m-0"
          collapsedLineLimit={6}
          expandedLineLimit={15}
          onExpandStateChange={setExpandState}
        />
        <div className="flex shrink-0 flex-col items-center self-stretch">
          {expandState?.showToggle && (
            <button
              type="button"
              onClick={expandState.toggle}
              className="mt-1 shrink-0 rounded p-1 text-ink transition-colors hover:text-ink"
              aria-label={expandState.isExpanded ? 'Collapse' : 'Expand'}
            >
              {expandState.isExpanded
                ? <ArrowsPointingInIcon className="size-5" />
                : <ArrowsPointingOutIcon className="size-5" />}
            </button>
          )}
          <Button
            type="button"
            styling="outline"
            color="light"
            rounding="none"
            className="mt-auto !h-11 !w-11 shrink-0 !rounded-full bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary"
            onClick={() => { void composer.submit(); }}
          >
            <PaperAirplaneIcon className="size-5 text-white" />
          </Button>
        </div>
      </div>
    </div>
  );

  const shouldShowChatView = (stream?.messages?.length ?? 0) > 0;

  return (
    <div className="flex min-h-full grow flex-col bg-surface-100">
      {shouldShowChatView ? (
        /* ── Chat view: messages above, form sticky at the bottom ── */
        <div className="flex min-h-0 grow flex-col">
          <div
            ref={messageContainerRef}
            className="container mx-auto flex min-h-0 grow flex-col-reverse gap-5 overflow-y-auto p-4 pb-6"
          >
            {(stream?.status === 'inflight') && <ChatTypingDots />}
            {messagesList}
          </div>

          <div className="bg-surface-100/95 backdrop-blur-sm">
            <form
              onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
              className="container mx-auto px-4 pb-4 pt-3"
            >
              <div className="mx-auto w-full max-w-4xl">
                {formElements}
              </div>
            </form>

            <CandidateFloatingNavBar
              locale={locale}
              selectedButton="coaching"
              pillBackground="surface-100"
              inline
              isAuthenticated={isAuthenticated}
            />
          </div>
        </div>
      ) : (
        /* ── Initial view: content bottom-aligned, max 50% width, centered ── */
        <div className="flex grow flex-col items-center justify-end pb-2 pt-6 lg:pt-16">
          <div className="flex w-full max-w-5xl flex-col items-center gap-4 px-4 text-center sm:px-6 lg:px-0">
            {/* Animated first-message bubble (appears while stream is loading) */}
            <Transition
              as="div"
              show={hasSubmittedFirstMessage && !!firstMessageContent}
              enter="transition-all duration-700 ease-out"
              enterFrom="opacity-0 translate-y-4 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              className="w-full"
            >
              <div className="ml-auto w-fit max-w-[80%] rounded-2xl bg-secondary px-4 py-2 text-white">
                {firstMessageContent}
              </div>
            </Transition>

            <span className="mb-2 w-full text-4xl font-bold leading-tight sm:text-5xl lg:text-[72px]">
              {t('homepage.hero.title')}
            </span>
            <span className="w-full max-w-4xl text-base !text-ink/75 sm:text-lg lg:text-2xl">
              {t('homepage.hero.subtitle.mc')}
            </span>

            <form
              onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
              className={cn(
                'w-full max-w-4xl px-7 transition-all duration-[400ms] ease-in-out',
                hasSubmittedFirstMessage && 'sticky bottom-0 z-10 bg-surface-100/95 py-3 backdrop-blur-sm',
              )}
            >
              {formElements}
            </form>

            <CandidateFloatingNavBar
              locale={locale}
              selectedButton="coaching"
              pillBackground="surface-100"
              inline
              isAuthenticated={isAuthenticated}
            />
          </div>
        </div>
      )}

      <ChatJobModal locale={locale} jobsArray={jobsArray} />
      <ChatTalentModal talentsArray={[{ items: talentsNavigationList }]} />
    </div>
  );
};
